
import React, { useContext, useEffect, useState } from 'react';
import { SectionHeader } from '../../common';
import { CaretDownFilled, CaretUpFilled, OrderedListOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Spin } from 'antd';
import classNames from 'classnames';
import { getParams, setParams, setSearchParam } from '../../../branding/utils/urlManipulation';
import { Translate } from '../../../services/translationService/components/Translate';
import { PageContainer, PageContent, PaginationController } from '../../common';
import LearningList from '../../common/learningList';
import './styles.less';
import { useSearchLazyQuery } from '../../../gql/hooks/queries/useSearchLazyQuery';
import { useTranslations } from '../../../hooks/translations';
import { BreadcrumbContext } from '../../../breadcrumbs/BreadcrumbContext';

const keys = {
    courses: 'courses',
    curriculum: 'curricula',
    grid: 'card',
    list: 'list',
    zToA: 'za',
    aToZ: 'az',
    weight: 'weight'
};

export const MyLearningPage = ( { pageKey } ) => {

    const initialUrlParams = getParams();

    const { locale } = useTranslations();

    const { leaveCrumb } = useContext( BreadcrumbContext );    
    
    useEffect( () => {leaveCrumb( pageKey );}, [] );
    
    const [ pageData, setPageData ] = useState( initialUrlParams.page ? { page: parseInt( initialUrlParams.page ) } : { page: 1 } );
    // these store the keys, not the values
    const [ filterInfo, setFilterInfo ] = useState( initialUrlParams.filter ?? keys.curriculum ); 
     
    const [ alpha, setAlpha ] = useState( initialUrlParams.sort || keys.weight ); 
    const [ view, setView ] = useState( initialUrlParams.view ?? keys.grid );

    const [ searchQuery, { data, loading: queryLoading, error, pageData: searchPageData } ] = useSearchLazyQuery( {}, false, false );

    const query = async ( page, sortAlpha, filter ) => { 
        searchQuery(
            sortAlpha !== keys.weight ?
            {
                types: [ filter ],
                query: '',
                page: page ?? 1,
                limit: 8, 
                sort: sortAlpha === keys.weight ? keys.weight : 'name',
                order: sortAlpha === keys.weight ? 'asc' : sortAlpha === keys.aToZ  ? 'asc' : 'desc',
            } 
            : 
            {
                types: [ filter ],
                query: '',
                page: page ?? 1,
                limit: 8, 
                    
                sortObject: [ 
                    { 
                        sort: keys.weight, 
                        order: 'asc' 
                    }, 
                    { 
                        sort: 'name',
                        order: 'asc'
                    } 
                ]
            }  
        );
    };

    const [ paginationController, setPaginationController ] = useState();

    function fetch( { pageNumber, sortAlpha = alpha, filter = filterInfo } ) {

        const params = {
            page: pageNumber ?? pageData?.page,
            sort: sortAlpha,
            filter: filter
        };

        setParams( params );
        
        query( params.page, sortAlpha, filter );
    }

    useEffect( () => {
        fetch( { } );
    }, [ locale ] );

    function onPageChange( page ) {

        setPageData( { ...pageData, page } );
        fetch( { pageNumber: page } );
    }

    function onFilterChange( filter ) {
        let newSort = alpha;

        if ( filterInfo === 'curricula' && filter === 'courses' ) {
            setAlpha( keys.aToZ );
            newSort = keys.aToZ;
        }
       
        paginationController?.setActivePage( 0 );    
        setFilterInfo( filter );
        fetch( { sortAlpha: newSort, filter, pageNumber: 1 } );
    }

    function selectFilter( opt ) {
        if ( filterInfo !== opt ) {
            onFilterChange( opt );
        }
    }

    function onSortChange() {
        let newSort = keys.weight;
        
        if ( filterInfo === 'curricula' ) {
            if ( alpha === keys.aToZ ) {
                newSort = keys.zToA;
            } else {
                if ( alpha === keys.zToA ) {
                    newSort = keys.weight;
                } else {
                    if ( alpha === keys.weight ) {
                        newSort = keys.aToZ;
                    }
                }
            }
        } else {
            if ( alpha === keys.aToZ ) {
                newSort = keys.zToA;
            } else {
                newSort = keys.aToZ;
            } 
        }

        paginationController?.setActivePage( 0 );
        setAlpha( newSort );
        fetch( { sortAlpha: newSort, pageNumber: 1 } );
    }

    function onViewChange( viewInfo ) {
        setView( viewInfo );
        setSearchParam( 'view', viewInfo );
    }

    useEffect( () => {
        if ( searchPageData.totalResults !== undefined && !queryLoading ) {
            setPageData( searchPageData );
        }
    }, [ searchPageData, queryLoading ] );

    return (
        <PageContent className="page-home">
            <PageContainer>
                <SectionHeader>
                    <Translate.Text value="page.myLearning.headers.myLearning" />
                </SectionHeader>
            

            <div className='learning-content'>
                <div className='controls'>
                    <div className='filter-controls non-compact'>

                            <Button shape='round' key={ keys.courses } onClick={ () => selectFilter( keys.courses ) } className={ classNames( { active: filterInfo == keys.courses }, 'control' ) }>
                                <Translate.Text value='control.courses'/>
                            </Button> 

                            <Button shape='round' key={ keys.curriculum } onClick={ () => selectFilter( keys.curriculum ) } className={ classNames( { active: filterInfo == keys.curriculum }, 'control' ) }>
                                <Translate.Text value='control.curriculum'/>
                            </Button> 
                    </div>
                    <div className='filter-controls compact'>
                        <Dropdown 
                            trigger={ [ 'click' ] }
                            overlay={
                                <Menu>
                                    <Menu.Item key={ keys.courses } 
                                        className={ classNames( 'selectable', { selected: ( filterInfo == keys.courses ) } ) }
                                        onClick={ () => selectFilter( keys.courses ) } >
                                        <Translate.Text value='control.courses'/>
                                    </Menu.Item>
                                    <Menu.Item key={ keys.curriculum } 
                                        className={ classNames( 'selectable', { selected: ( filterInfo == keys.curriculum ) } ) }
                                        onClick={ () => selectFilter( keys.curriculum ) } >
                                        <Translate.Text value='control.curriculum'/>
                                    </Menu.Item>
                                </Menu>
                            }
                        >
                            <Button shape='round' className={ classNames( 'active', 'control' ) } >
                                <span className='button-text'>
                                    { filterInfo == keys.courses ? <Translate.Text value='control.courses'/> : <Translate.Text value='control.curriculum'/> }
                                    <CaretDownFilled />
                                </span>
                            </Button>
                                
                        </Dropdown>
                    </div>

                    <div className='sort-controls'>
                        <Button 
                            shape='round' 
                            className={ 'active control' } 
                            onClick={ 
                                () => onSortChange()
                            } 
                        >
                            { alpha === keys.aToZ &&
                                <><Translate.Text value="control.alphabetical" /><CaretDownFilled /></>
                            }
                            
                            { alpha === keys.zToA &&
                                <><Translate.Text value="control.antiAlphabetical" /><CaretUpFilled /></> 
                            }
                            
                            { ( filterInfo === 'curricula' && alpha === keys.weight ) && 
                                <><OrderedListOutlined style={ { fontSize: 'xx-large' } }/><CaretDownFilled /></>
                            }
                        </Button> 
                    </div>

                    <div className='view-controls compact desktop-only'>
                        <Dropdown 
                            trigger={ [ 'click' ] }
                            overlay={
                                <Menu>
                                    <Menu.Item key={ keys.list } 
                                        className={ classNames( 'selectable', { selected: ( view == keys.list ) } ) }
                                        onClick={ () => onViewChange( keys.list ) } >
                                        <Translate.Text value='courseList.viewOptions.list.label'/>
                                    </Menu.Item>
                                    <Menu.Item key={ keys.grid } 
                                        className={ classNames( 'selectable', { selected: ( view == keys.grid ) } ) }
                                        onClick={ () => onViewChange( keys.grid ) } >
                                        <Translate.Text value='courseList.viewOptions.grid.label'/>
                                    </Menu.Item>
                                </Menu>
                            }
                        >
                            <Button shape='round' className={ classNames( 'active', 'control' ) } >
                                <span className='button-text'>
                                    { view == keys.grid ? <Translate.Text value='courseList.viewOptions.grid.label'/> : <Translate.Text value='courseList.viewOptions.list.label'/> }
                                    <CaretDownFilled />
                                </span>
                            </Button>
                                
                        </Dropdown>
                    </div>
                    <div className='view-controls non-compact desktop-only'>

                        <Button shape='round' key={ keys.list } onClick={ () => onViewChange( keys.list ) } className={ classNames( { active: view == keys.list }, 'control' ) }>
                            <Translate.Text value='courseList.viewOptions.list.label'/>
                        </Button> 

                        <Button shape='round' key={ keys.grid } onClick={ () => onViewChange( keys.grid ) } className={ classNames( { active: view == keys.grid }, 'control' ) }>
                            <Translate.Text value='courseList.viewOptions.grid.label'/>
                        </Button> 
                    </div>

                </div>
                <div className={ classNames( 'search-results', { empty: data?.length === 0 && error } ) }>
                    {
                        queryLoading || data == null ? <Spin /> : 
                            data?.length === 0 && error ? <div className='empty-page no-border'><Translate.Text className='title' value='page.searchLibrary.empty' /></div> : // nothing is returned from search
                            <LearningList
                                displayType={ view }
                                learningType={ filterInfo == keys.courses ? 'course' : 'curriculum' }
                                data={ data }
                                isLoading={ queryLoading }
                                />
                    }
                </div>
            </div>

            <div className={ classNames( 'learning-list-pagination-controller', { hidden: data?.length === 0 || error } ) }>
                { pageData?.totalResults > 0 &&
                    <PaginationController
                        pageSize={ pageData.limit }
                        initialPage={ pageData.page - 1 }
                        itemCount={ pageData.totalResults }
                        onChange={ ( index ) => onPageChange( index + 1 ) }
                        getController={ setPaginationController }
                    />
                }
                        
                </div>
                
            </PageContainer>
       </PageContent>
    );
};
